"use client"

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { RefreshCw, TrendingUp, PieChart, Globe, BarChart3, Monitor, Smartphone, Tablet } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { getEssentialAnalytics } from "@/services/analytics-dashboard-service"
import {
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts"
import ReactCountryFlag from "react-country-flag"

interface AnalyticsChartsProps {
  previewId: string
  className?: string
  onRefresh?: () => void
  showSessionCharts?: boolean
}

const CHART_COLORS = {
  primary: "#6c9956",
  secondary: "#8B5CF6",
  accent: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444"
}

const PIE_COLORS = {
  desktop: "#6c9956",
  mobile: "#06B6D4", 
  tablet: "#8B5CF6",
  unknown: "#94A3B8"
}

const getDeviceIcon = (device: string) => {
  switch (device.toLowerCase()) {
    case 'desktop':
      return <Monitor className="h-4 w-4" />
    case 'mobile':
      return <Smartphone className="h-4 w-4" />
    case 'tablet':
      return <Tablet className="h-4 w-4" />
    default:
      return <Monitor className="h-4 w-4" />
  }
}

const getDeviceColor = (device: string) => {
  switch (device.toLowerCase()) {
    case 'desktop':
      return PIE_COLORS.desktop
    case 'mobile':
      return PIE_COLORS.mobile
    case 'tablet':
      return PIE_COLORS.tablet
    default:
      return PIE_COLORS.unknown
  }
}

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }: any) => {
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="fill-white text-sm font-medium"
      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
    >
      {`${percentage}%`}
    </text>
  )
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          {getDeviceIcon(data.device)}
          <span className="font-medium capitalize">{data.device}</span>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Views:</span>
            <span className="font-medium">{data.count}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Percentage:</span>
            <span className="font-medium">{data.percentage}%</span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export default function AnalyticsCharts({ previewId, className, onRefresh }: AnalyticsChartsProps) {
  const { t } = useTranslation("analytics")
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchChartsData = useCallback(async () => {
    try {
      const data = await getEssentialAnalytics(previewId)
      setAnalyticsData(data)
    } catch (error) {
      console.error('Error fetching charts data:', error)
      toast.error(t("analytics:charts.error"))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [previewId, t])

  useEffect(() => {
    if (previewId) {
      fetchChartsData()
    }
  }, [previewId, fetchChartsData])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await fetchChartsData()
    onRefresh?.()
    toast.success(t("analytics:charts.refreshed"))
  }, [fetchChartsData, onRefresh, t])

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{t("analytics:charts.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("analytics:charts.description")}</p>
          </div>
          <Button variant="outline" size="sm" disabled>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("analytics:charts.refresh")}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-60" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className={`space-y-6 ${className}`}>
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("analytics:charts.noData")}</h3>
            <p className="text-muted-foreground">{t("analytics:charts.noDataDescription")}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("analytics:charts.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("analytics:charts.description")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {t("analytics:charts.refresh")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. Views Timeline - Area Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              {t("analytics:charts.timeline.title")}
            </CardTitle>
            <CardDescription>{t("analytics:charts.timeline.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analyticsData.timelineData}>
                <defs>
                  <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  className="text-xs fill-muted-foreground"
                />
                <YAxis className="text-xs fill-muted-foreground" />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value) => [value, t("analytics:charts.timeline.views")]}
                />
                <Area
                  type="monotone"
                  dataKey="views"
                  stroke={CHART_COLORS.primary}
                  fillOpacity={1}
                  fill="url(#viewsGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 2. Device Distribution - Enhanced Pie Chart */}
       <Card>
  <CardHeader>
    <CardTitle className="flex items-center gap-2">
      <PieChart className="h-5 w-5" />
      {t("analytics:charts.devices.title")}
    </CardTitle>
    <CardDescription>{t("analytics:charts.devices.description")}</CardDescription>
  </CardHeader>
  <CardContent>
    <div className="space-y-6">
      {/* Enhanced Pie Chart */}
      <ResponsiveContainer width="100%" height={260}>
        <RechartsPieChart>
          <Pie
            data={analyticsData.deviceData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={90}
            labelLine={false}
            label={CustomPieLabel}
            dataKey="count"
            paddingAngle={2}
            isAnimationActive={true}
          >
            {analyticsData.deviceData.map((entry: any, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={getDeviceColor(entry.device)}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </RechartsPieChart>
      </ResponsiveContainer>

      {/* Device Statistics */}
      <div className="grid gap-3 pt-2 border-t border-border">
        {analyticsData.deviceData.map((entry: any, index: number) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: getDeviceColor(entry.device) }}
              />
              <span className="text-sm font-medium capitalize">{entry.device}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{entry.count} views</span>
              <span className="text-sm font-semibold text-foreground">{entry.percentage}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </CardContent>
</Card>

        {/* 4. Top Countries - Horizontal Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t("analytics:charts.geographic.title")}
            </CardTitle>
            <CardDescription>{t("analytics:charts.geographic.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analyticsData.topCountries.map((country: any, index: number) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-24">
                    <ReactCountryFlag
                      countryCode={country.country.toUpperCase()}
                      svg
                      style={{
                        width: "1.2em",
                        height: "1.2em",
                        borderRadius: "2px",
                      }}
                    />
                    <span className="text-sm font-medium">{country.country}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-3">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-purple-400  to-purple-600"
                        style={{ width: `${country.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-16 text-right">
                      {country.count} ({country.percentage}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  )
}